// @noflow
import addMobileNavHelperListeners from '@/helpers/MobileNavHelper'
import initI18n from '@/packs/localisation'
import 'intersection-observer'
import React from 'react'
import ReactDOM from 'react-dom'

import CharityCarousel from '@/components/carousel/CharityCarousel'
import CustomerStoriesCarousel from '@/components/carousel/ugc_reviews_carousel'
import { initCookieConsent } from '@/components/cookie_consent'
import LearnMoreSection from '@/components/shared/LearnMoreAboutButternut'
import { initFooterLeadCapture } from '@/components/shared/elements/FooterLeadCapture/FooterLeadCapture'
import initGeoIpWidget from '@/components/shared/geo_ip_widget/initGeoIpWidget'

import { Code as CountryCode, Language } from '@/types'

import { customerStories } from './staticContent/customerStories'

const initCustomerStoriesCarousel = (): void => {
  const customerStoriesSectionElement = document.querySelector(
    '.ugc-stories__carousel-container'
  )
  if (!customerStoriesSectionElement)
    throw new Error('Could not find customer stories section element')

  if (!customerStories || customerStories.length < 1)
    throw new Error('Could not find reviews')

  ReactDOM.render(
    <CustomerStoriesCarousel
      customerStories={customerStories}
      namespace="our_story"
    />,
    customerStoriesSectionElement
  )
}

const initLearnMoreSection = (): void => {
  const learnMoreSectionElement = document.getElementById('learn-more-section')
  if (!learnMoreSectionElement) return

  const learnMoreSectionComponent = (
    <LearnMoreSection
      tiles={['making-our-meals', 'how-it-works', 'our-products']}
    />
  )

  ReactDOM.render(learnMoreSectionComponent, learnMoreSectionElement)
}

const initTodayCarousel = (): void => {
  const carouselElement = document.getElementsByClassName('today__carousel')[0]

  if (!carouselElement) {
    throw new Error(
      `Couldn't find the carousel element for the Butternut Today section`
    )
  }

  ReactDOM.render(<CharityCarousel />, carouselElement)
}

const initPolariodAnimation = (): void => {
  const polaroidObserver = (
    entries: Array<IntersectionObserverEntry>
  ): void => {
    entries.forEach((entry: IntersectionObserverEntry): void => {
      if (entry.intersectionRatio === 1) {
        if (entry.target.classList.contains('polaroid--double')) {
          const polaroids = Array.from(
            entry.target.querySelectorAll('.polaroid')
          )
          polaroids[0].classList.add('animate--left')
          polaroids[1].classList.add('animate--right')
        } else {
          const polaroid = entry.target.querySelector('.polaroid')
          polaroid && polaroid.classList.add('animate--center')
        }
      } else {
        const polaroids = Array.from(entry.target.querySelectorAll('.polaroid'))
        polaroids[0].classList.remove('animate--left')
        polaroids[0].classList.remove('animate--center')
        if (polaroids.length > 1) {
          polaroids[1].classList.remove('animate--right')
        }
      }
    })
  }
  const intersectionObserver = new IntersectionObserver(polaroidObserver, {
    threshold: [0, 1]
  })
  const polaroids = document.querySelectorAll('.polaroid-container')
  if (!polaroids) throw new Error('No polaroids found')

  for (let i = 0; i < polaroids.length; i++) {
    intersectionObserver.observe(polaroids[i])
  }
}

const fadeStepsIn = (): void => {
  const stepObserver = (entries: Array<IntersectionObserverEntry>): void => {
    entries.forEach((entry: IntersectionObserverEntry): void => {
      if (entry.intersectionRatio >= 0.25) {
        entry.target.classList.add('step--visible')
        entry.target.classList.remove('step--hidden')
      } else if (
        entry.intersectionRatio === 0 &&
        entry.boundingClientRect.y > 0
      ) {
        entry.target.classList.add('step--hidden')
        entry.target.classList.remove('step--visible')
      }
    })
  }
  const intersectionObserver = new IntersectionObserver(stepObserver, {
    threshold: [0, 0.25]
  })

  const steps = document.querySelectorAll('.step')
  if (!steps) throw new Error('No steps found')

  for (let i = 0; i < steps.length; i++) {
    intersectionObserver.observe(steps[i])
  }
}

const contentTag = document.querySelector(
  '#our-story-content-tag'
) as HTMLElement
if (!contentTag) {
  throw new Error(`Couldn't find the our-story-content-tag element`)
}
const { dataset } = contentTag
const { language, shippingCountry, isPsibufet } = dataset

const countryCode: CountryCode =
  (shippingCountry as CountryCode) || CountryCode.GB

const initLanguage = (): void => {
  const preferredLanguage: Language = (language as Language) || Language.en

  initI18n(preferredLanguage)
}

document.addEventListener('DOMContentLoaded', (): void => {
  initLanguage()
  initLearnMoreSection()
  if (!isPsibufet) {
    initTodayCarousel()
  }
  addMobileNavHelperListeners()
  initCookieConsent()
  initLearnMoreSection()
  if (countryCode !== CountryCode.BE) {
    initCustomerStoriesCarousel()
  }
  initGeoIpWidget({
    desktopNav: true,
    mobileSidebar: true,
    contentTagElementId: 'our-story-content-tag'
  })
  if (!window.matchMedia('(prefers-reduced-motion)').matches) {
    initPolariodAnimation()
    fadeStepsIn()
  }
  initFooterLeadCapture()
})
